// src/pages/Billing.tsx
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  useIonToast,
} from '@ionic/react';
import { checkmarkCircle, sparkles } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TitleBar from '../components/TitleBar.tsx';
import { billing } from '../services/api.ts';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  price_display: string;
  popular: boolean;
}

const Billing: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [present] = useIonToast();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);

  // Add shimmer animation for premium button
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    loadPackages();

    // Check if returning from successful purchase
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');

    if (status === 'success' && sessionId) {
      verifyPurchase(sessionId);
    } else if (status === 'cancelled') {
      present({
        message: 'Purchase cancelled. No charges were made.',
        duration: 3000,
        color: 'warning',
      });
      // Clean up URL
      history.replace('/app/settings/billing');
    }
  }, [location]);

  const loadPackages = async () => {
    try {
      const response = await billing.getPackages();
      setPackages(response.packages || []);
    } catch (error) {
      console.error('Failed to load packages:', error);
      present({
        message: 'Failed to load credit packages',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyPurchase = async (sessionId: string) => {
    try {
      await billing.verifyPurchase(sessionId);
      present({
        message: 'Credits added successfully!',
        duration: 3000,
        color: 'success',
      });
      // Clean up URL
      history.replace('/app/settings/billing');
    } catch (error: any) {
      console.error('Failed to verify purchase:', error);

      // Extract more detailed error info
      const errorMsg = error.response?.data?.detail
        || error.response?.data?.message
        || error.message
        || 'Purchase verification failed';

      const statusCode = error.response?.status;

      present({
        message: `Error ${statusCode ? `(${statusCode})` : ''}: ${errorMsg}. Please contact support if credits were not added.`,
        duration: 7000,
        color: 'danger',
      });
    }
  };

  const handlePurchase = async (packageId: string) => {
    setPurchasing(packageId);
    try {
      const response = await billing.createCheckoutSession(packageId);
      // Redirect to Stripe checkout
      window.location.href = response.checkout_url;
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      present({
        message: error.response?.data?.detail || 'Failed to start checkout process',
        duration: 3000,
        color: 'danger',
      });
      setPurchasing(null);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <TitleBar title="Billing" />
      </IonHeader>
      <IonContent className="ion-padding">
        <IonGrid>
          <IonRow className="ion-justify-content-center">
            <IonCol size="12" sizeMd="10" sizeLg="8">
              <IonText className="ion-text-center">
                <h2 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Purchase AI Credits</h2>
                <p style={{ color: '#666', marginBottom: '32px' }}>
                  Choose a credit package to power your AI features
                </p>
              </IonText>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <IonSpinner />
                </div>
              ) : (
                <IonGrid>
                  <IonRow>
                    {packages.map((pkg) => (
                      <IonCol key={pkg.id} size="12" sizeMd="4" style={{ paddingTop: '8px' }}>
                        <IonCard
                          style={{
                            borderRadius: '16px',
                            border: pkg.popular ? '2px solid #3880ff' : '1px solid #e0e0e0',
                            position: 'relative',
                            height: '100%',
                            paddingTop: pkg.popular ? '16px' : '0'
                          }}
                        >
                          {pkg.popular && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#3880ff',
                                color: 'white',
                                padding: '6px 20px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(56, 128, 255, 0.3)',
                                zIndex: 10,
                                letterSpacing: '0.5px'
                              }}
                            >
                              POPULAR
                            </div>
                          )}
                          <IonCardHeader style={{ paddingTop: pkg.popular ? '56px' : '24px', paddingBottom: '12px' }}>
                            <IonText className="ion-text-center">
                              <h3 style={{ fontWeight: 'bold', fontSize: '20px', marginBottom: '8px' }}>
                                {pkg.name}
                              </h3>
                              <div style={{ marginBottom: '16px' }}>
                                <span
                                  style={{
                                    fontSize: '36px',
                                    fontWeight: 'bold',
                                    color: '#000',
                                  }}
                                >
                                  {pkg.price_display}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#666',
                                }}
                              >
                                <IonIcon icon={sparkles} style={{ marginRight: '6px', color: '#FFD700' }} />
                                <span style={{ fontSize: '18px', fontWeight: '600' }}>
                                  {pkg.credits.toLocaleString()} credits
                                </span>
                              </div>
                            </IonText>
                          </IonCardHeader>
                          <IonCardContent style={{ paddingTop: '8px', paddingBottom: '24px' }}>
                            <IonGrid>
                              <IonRow className="ion-justify-content-center">
                                <IonCol size="12">
                                  <div style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                      <IonIcon icon={checkmarkCircle} style={{ marginRight: '8px', color: '#10dc60' }} />
                                      <span style={{ fontSize: '14px' }}>Resume analysis</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                      <IonIcon icon={checkmarkCircle} style={{ marginRight: '8px', color: '#10dc60' }} />
                                      <span style={{ fontSize: '14px' }}>Cover letter generation</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                      <IonIcon icon={checkmarkCircle} style={{ marginRight: '8px', color: '#10dc60' }} />
                                      <span style={{ fontSize: '14px' }}>Job fit analysis</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <IonIcon icon={checkmarkCircle} style={{ marginRight: '8px', color: '#10dc60' }} />
                                      <span style={{ fontSize: '14px' }}>Job search</span>
                                    </div>
                                  </div>
                                  <IonButton
                                    expand="block"
                                    className="ion-no-border"
                                    onClick={() => handlePurchase(pkg.id)}
                                    disabled={purchasing !== null}
                                    style={{
                                      '--border-radius': '12px',
                                      height: '48px',
                                      fontWeight: '700',
                                      fontSize: '16px',
                                      textTransform: 'none',
                                      '--background': pkg.popular ? '#7E22CE' : '#1E40AF',
                                      '--background-activated': pkg.popular ? '#6B21A8' : '#1E3A8A',
                                      '--background-hover': pkg.popular ? '#9333EA' : '#2563EB',
                                      '--box-shadow': 'none',
                                      '--color': 'white',
                                      '--color-activated': 'white',
                                      '--color-hover': 'white',
                                      '--border-width': '0px',
                                      '--border-style': 'none',
                                      '--border-color': 'transparent',
                                      '--ripple-color': 'transparent',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      transition: 'all 0.3s ease',
                                      border: '0',
                                      outline: '0',
                                      filter: 'saturate(1.3) contrast(1.1)',
                                      WebkitAppearance: 'none',
                                      MozAppearance: 'none',
                                    }}
                                    onFocus={(e: any) => {
                                      e.currentTarget.style.outline = 'none';
                                      e.currentTarget.style.border = 'none';
                                    }}
                                    onMouseEnter={(e: any) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.outline = 'none';
                                      e.currentTarget.style.border = 'none';
                                    }}
                                    onMouseLeave={(e: any) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.outline = 'none';
                                      e.currentTarget.style.border = 'none';
                                    }}
                                  >
                                    {purchasing === pkg.id ? (
                                      <IonSpinner name="crescent" style={{ color: 'white' }} />
                                    ) : (
                                      <>
                                        {pkg.popular && (
                                          <span style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                                            animation: 'shimmer 2s infinite',
                                          }} />
                                        )}
                                        <span style={{ position: 'relative', zIndex: 1 }}>Purchase</span>
                                      </>
                                    )}
                                  </IonButton>
                                </IonCol>
                              </IonRow>
                            </IonGrid>
                          </IonCardContent>
                        </IonCard>
                      </IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
              )}

              <IonText className="ion-text-center" style={{ marginTop: '32px' }}>
                <p style={{ fontSize: '12px', color: '#999' }}>
                  Secure payment powered by Stripe. Credits never expire.
                </p>
              </IonText>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default Billing;
